import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Terminal, CheckCircle2 } from "lucide-react";

const LOGS = [
  { text: "New lead received from landing page form...", tone: "cyan" },
  { text: "Parsing fields: name, email, phone, business...", tone: "violet" },
  { text: "Sending follow-up email via Resend API...", tone: "blue" },
  { text: "Saving to system: MongoDB + Google Sheets...", tone: "cyan" },
  { text: "Routing to team dashboard...", tone: "violet" },
  { text: "Completed.", tone: "success" },
];

const toneColor = {
  cyan: "text-[#00F0FF]",
  violet: "text-[#8B5CF6]",
  blue: "text-[#007AFF]",
  success: "text-emerald-400",
};

function useTyping(active) {
  const [lines, setLines] = useState([]);
  const [typed, setTyped] = useState("");
  const idx = useRef(0);
  const ch = useRef(0);

  useEffect(() => {
    if (!active) return;
    let t;
    const tick = () => {
      const i = idx.current;
      if (i >= LOGS.length) {
        t = setTimeout(() => {
          idx.current = 0;
          ch.current = 0;
          setLines([]);
          setTyped("");
          tick();
        }, 2200);
        return;
      }
      const current = LOGS[i].text;
      if (ch.current <= current.length) {
        setTyped(current.slice(0, ch.current));
        ch.current += 1;
        t = setTimeout(tick, 22);
      } else {
        setLines((prev) => [...prev, LOGS[i]]);
        setTyped("");
        idx.current += 1;
        ch.current = 0;
        t = setTimeout(tick, 420);
      }
    };
    tick();
    return () => clearTimeout(t);
    // LOGS is a module-level constant; idx/ch are refs (stable). Only `active`
    // should retrigger this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  return { lines, typed, currentIdx: idx.current };
}

export default function LiveDemo() {
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setVisible(true)),
      { threshold: 0.2 }
    );
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
    // Mount-only: sectionRef is stable, IntersectionObserver is a global.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { lines, typed, currentIdx } = useTyping(visible);
  const progress = Math.min(100, Math.round(((lines.length) / LOGS.length) * 100));

  return (
    <section id="demo" ref={sectionRef} className="relative py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-6">
        <div className="max-w-3xl mb-12">
          <p className="text-xs uppercase tracking-[0.25em] text-[#00F0FF] mb-4" data-testid="demo-kicker">Live System Demo</p>
          <h2 className="font-display text-4xl sm:text-5xl font-medium text-white leading-tight" data-testid="demo-heading">
            Watch a lead move through your pipeline in real time.
          </h2>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7 }}
          className="relative max-w-5xl mx-auto glass-card overflow-hidden"
          data-testid="demo-dashboard"
        >
          {/* Window chrome */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 bg-black/40">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-red-500/70" />
              <span className="w-3 h-3 rounded-full bg-yellow-500/70" />
              <span className="w-3 h-3 rounded-full bg-emerald-500/70" />
            </div>
            <div className="flex items-center gap-2 text-white/50 text-xs font-mono">
              <Terminal size={14} />
              vyomex://pipeline/leads
            </div>
            <div className="flex items-center gap-2 text-xs">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-emerald-300">Live</span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 divide-x divide-white/10">
            {/* Log panel */}
            <div className="lg:col-span-2 p-6 font-mono text-sm min-h-[380px] bg-black/50">
              <div className="space-y-2">
                {lines.map((l, i) => (
                  <div key={`log-${i}-${l.text}`} className="flex items-start gap-3">
                    <span className="text-white/30">[{String(i + 1).padStart(2, "0")}]</span>
                    <span className={toneColor[l.tone]}>{l.text}</span>
                  </div>
                ))}
                {typed && (
                  <div className="flex items-start gap-3">
                    <span className="text-white/30">[{String(lines.length + 1).padStart(2, "0")}]</span>
                    <span className={toneColor[LOGS[currentIdx]?.tone] || "text-white"}>
                      {typed}
                      <span className="inline-block w-2 h-4 bg-[#00F0FF] ml-1 animate-blink align-middle" />
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Stats panel */}
            <div className="p-6 space-y-6 bg-black/30">
              <div>
                <div className="flex items-center justify-between text-xs uppercase tracking-[0.18em] text-white/50 mb-2">
                  <span>Pipeline Progress</span>
                  <span>{progress}%</span>
                </div>
                <div className="h-2 w-full rounded-full bg-white/10 overflow-hidden">
                  <div className="h-full progress-shimmer transition-all duration-500" style={{ width: `${progress}%` }} />
                </div>
              </div>

              <div className="space-y-3">
                {[
                  { label: "Capture", color: "bg-[#00F0FF]" },
                  { label: "Enrich", color: "bg-[#007AFF]" },
                  { label: "Follow-up", color: "bg-[#8B5CF6]" },
                  { label: "Sync", color: "bg-emerald-400" },
                ].map((s, i) => {
                  const done = lines.length > i;
                  return (
                    <div key={s.label} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className={`w-2.5 h-2.5 rounded-full ${s.color} ${done ? "" : "opacity-30"} ${done ? "shadow-[0_0_12px_currentColor]" : ""}`} />
                        <span className={`text-sm ${done ? "text-white" : "text-white/40"}`}>{s.label}</span>
                      </div>
                      {done && <CheckCircle2 size={14} className="text-emerald-400" />}
                    </div>
                  );
                })}
              </div>

              <div className="pt-4 border-t border-white/10 text-xs text-white/40 font-mono">
                Avg response time<br />
                <span className="text-2xl font-display font-medium text-white">{progress < 100 ? "—" : "12s"}</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
